/**
 * FlexCard ✦
 *
 * 9:16 share artwork (1080×1920 native, scaled responsively).
 * Pure DOM — exported to PNG via html-to-image. Spotify-Wrapped
 * energy, NOT LinkedIn certificate. Built to feel screenshot-
 * worthy with animated halos, shimmer sweep, sparkle particles
 * and a glow medallion.
 *
 * All copy + visuals derive from the kind/badge/level inputs so
 * one card serves badge unlocks, level-ups, first shifts, and
 * reference flexes without forking the component.
 */
import React from 'react';
import { Trophy, Sparkle, Star } from '@phosphor-icons/react';

const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

// 9 × 16 ratio. Native 540 wide keeps DOM light; html-to-image
// will be invoked at pixelRatio=2 → 1080×1920 PNG out the other
// end (Instagram-Story crisp).
const W = 540;
const H = 960;

const SPARKLE_POSITIONS = [
  { x: 12, y: 18, size: 14, color: '#FFD166' },
  { x: 88, y: 24, size: 10, color: PINK },
  { x: 18, y: 64, size: 8,  color: BLUE },
  { x: 90, y: 68, size: 12, color: '#FFFFFF' },
  { x: 28, y: 86, size: 9,  color: PINK },
  { x: 78, y: 90, size: 11, color: BLUE },
];

const headlineFor = (kind, payload) => {
  const name = (payload.name || 'Crew').split(' ')[0];
  switch (kind) {
    case 'badge':     return `${name} just unlocked ${payload.label} ✦`;
    case 'level':     return `${name} levelled up ✦`;
    case 'first':     return `${name}'s first shift ✦`;
    case 'reference': return `${name} just earned a reference ✦`;
    default:          return `${name} on EventDrop Crew ✦`;
  }
};

const eyebrowFor = (kind) => {
  switch (kind) {
    case 'badge':     return '✦ BADGE UNLOCKED';
    case 'level':     return '✦ LEVELLED UP';
    case 'first':     return '✦ FIRST SHIFT';
    case 'reference': return '✦ NEW REFERENCE';
    default:          return '✦ EVENTDROP CREW';
  }
};

const subtitleFor = (kind, payload) => {
  if (kind === 'badge')   return payload.blurb || '';
  if (kind === 'level')   return `Level ${payload.level} Crew Member`;
  if (kind === 'first')   return 'Started with zero experience.';
  if (kind === 'reference') return 'Building confidence one event at a time.';
  return '';
};

const FlexCard = React.forwardRef(({ kind = 'badge', payload = {} }, ref) => {
  const headline = headlineFor(kind, payload);
  const eyebrow = eyebrowFor(kind);
  const subtitle = subtitleFor(kind, payload);
  const level = payload.level || 1;

  return (
    <div
      ref={ref}
      data-testid="flex-card"
      style={{
        width: W, height: H, position: 'relative', overflow: 'hidden',
        fontFamily: FONT_STACK, color: '#fff',
        background: `radial-gradient(circle at 18% 14%, ${PURPLE} 0%, transparent 55%),
                     radial-gradient(circle at 82% 88%, ${PINK} 0%, transparent 50%),
                     radial-gradient(circle at 50% 50%, ${BLUE}33 0%, transparent 70%),
                     linear-gradient(180deg, #0A0820 0%, #06061A 100%)`,
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
      }}
    >
      {/* Grain / noise — tiny SVG turbulence overlay */}
      <svg style={{ position: 'absolute', inset: 0, opacity: 0.05, mixBlendMode: 'overlay', pointerEvents: 'none' }}
        width={W} height={H}>
        <filter id="flexNoise">
          <feTurbulence baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
        </filter>
        <rect width={W} height={H} filter="url(#flexNoise)" />
      </svg>

      {/* Sparkles scattered across the canvas */}
      {SPARKLE_POSITIONS.map((s, i) => (
        <Sparkle
          key={i} size={s.size} weight="fill" color={s.color}
          style={{
            position: 'absolute',
            top: `${s.y}%`, left: `${s.x}%`,
            filter: `drop-shadow(0 0 6px ${s.color})`,
            opacity: 0.85,
          }}
        />
      ))}

      {/* TOP — EventDrop Crew logo */}
      <div style={{
        position: 'absolute', top: 36, left: 0, right: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      }}>
        <span style={{
          padding: '6px 14px', borderRadius: 999,
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.14)',
          fontSize: 13, fontWeight: 800, letterSpacing: '0.28em',
          textTransform: 'uppercase',
          backdropFilter: 'blur(6px)',
        }}>
          EventDrop Crew ✦
        </span>
      </div>

      {/* MIDDLE — Badge medallion */}
      <div style={{
        position: 'absolute', top: 170, left: 0, right: 0,
        display: 'flex', justifyContent: 'center',
      }}>
        <div style={{
          position: 'relative',
          width: 260, height: 260, borderRadius: '50%',
          background: `radial-gradient(circle at 35% 30%, ${PINK} 0%, ${PURPLE} 70%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 60px ${PINK}88, 0 0 120px ${PURPLE}55, inset 0 -20px 40px rgba(0,0,0,0.25)`,
        }}>
          {/* Rotating halo ring */}
          <div style={{
            position: 'absolute', inset: -14, borderRadius: '50%',
            border: `3px solid ${BLUE}`,
            borderRightColor: 'transparent', borderBottomColor: 'transparent',
            opacity: 0.85,
          }} />
          <div style={{
            position: 'absolute', inset: -28, borderRadius: '50%',
            border: `1px dashed ${PINK}66`,
          }} />
          <Trophy size={120} weight="fill" color="#FFFFFF" />
          {/* Shimmer sweep */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.35) 50%, transparent 65%)',
            mixBlendMode: 'screen',
          }} />
          <Star size={28} weight="fill" color="#FFD166"
            style={{ position: 'absolute', top: 18, right: 24, filter: 'drop-shadow(0 0 8px #FFD166)' }} />
          <Sparkle size={20} weight="fill" color="#FFFFFF"
            style={{ position: 'absolute', bottom: 22, left: 30, filter: 'drop-shadow(0 0 8px #fff)' }} />
        </div>
      </div>

      {/* Eyebrow */}
      <div style={{
        position: 'absolute', top: 470, left: 0, right: 0,
        textAlign: 'center',
      }}>
        <span style={{
          fontSize: 13, fontWeight: 800, letterSpacing: '0.36em',
          background: `linear-gradient(135deg, ${BLUE}, ${PINK})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          {eyebrow}
        </span>
      </div>

      {/* Headline */}
      <div style={{
        position: 'absolute', top: 510, left: 36, right: 36,
        textAlign: 'center',
      }}>
        <h1 style={{
          margin: 0, fontSize: 42, fontWeight: 900, lineHeight: 1.08,
          letterSpacing: '-0.025em', color: '#FFFFFF',
          textShadow: `0 4px 24px ${PURPLE}AA`,
        }}>
          {headline}
        </h1>
      </div>

      {/* Subtitle / blurb */}
      {subtitle && (
        <div style={{
          position: 'absolute', top: 650, left: 60, right: 60,
          textAlign: 'center',
        }}>
          <p style={{
            margin: 0, fontSize: 18, lineHeight: 1.45,
            color: '#D8D8F0', fontStyle: 'italic',
          }}>
            {subtitle}
          </p>
        </div>
      )}

      {/* Level chip */}
      <div style={{
        position: 'absolute', bottom: 168, left: 0, right: 0,
        textAlign: 'center',
      }}>
        <span style={{
          display: 'inline-block', padding: '14px 28px', borderRadius: 999,
          background: `linear-gradient(135deg, ${PURPLE}55, ${PINK}55)`,
          border: `1px solid ${PINK}88`,
          fontSize: 18, fontWeight: 900, letterSpacing: '0.04em',
          boxShadow: `0 8px 24px ${PURPLE}55`,
          color: '#fff',
        }}>
          Level {level} Crew Member
        </span>
      </div>

      {/* FOOTER — CTA */}
      <div style={{
        position: 'absolute', bottom: 64, left: 0, right: 0,
        textAlign: 'center',
      }}>
        <p style={{
          margin: '0 0 6px', fontSize: 22, fontWeight: 900,
          letterSpacing: '-0.015em', color: '#FFFFFF',
        }}>
          Join the Crew
        </p>
        <p style={{
          margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: '0.32em',
          textTransform: 'uppercase', color: BLUE,
        }}>
          eventdrop.co/crew
        </p>
      </div>
    </div>
  );
});

FlexCard.displayName = 'FlexCard';
export default FlexCard;
